import { Injectable } from '@angular/core';

import { APIHandler } from '../api-handler/api-handler';

import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';

import {
  ClickstreamActivityData,
  ClickstreamStore,
  ClickstreamEventData,
} from '../../types';
import { ClickstreamEventTypeName } from '../../enums/clickstream-events';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { LoggingService } from 'src/app/shared/services/logging/logging.service';

@Injectable()
export class ClickstreamAPI {
  readonly pathBase = `clickstream`;
  readonly clickstreamData = 'clickstream-data';

  userId!: number;
  schoolYear = -1;
  sessionId = '';
  authToken = '';

  private startTime = 0;
  private apiHandler: APIHandler;
  private activityData: ClickstreamActivityData = {
    activityUuid: '',
    contentOid: 0,
    activityName: '',
    category: '',
    difficulty: 0,
    scoreCriteria: '3',
  };
  private clickstreamCache?: ClickstreamStore;

  constructor(
    private configService: ConfigurationService,
    private messageService: MessageService,
    private loggingService: LoggingService
  ) {
    this.apiHandler = new APIHandler(
      {
        baseUrl: this.configService.config.olpApiDomain,
        defaultHeaders: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
      this.configService.config.configId
    );
    this.apiHandler.failSilently = true;
  }

  get currentActivity(): ClickstreamActivityData {
    return this.activityData;
  }

  initSchoolYear(year: number): void {
    this.schoolYear = year;
  }

  initStartTime(startTime: number): void {
    this.startTime = startTime;
  }

  getStartTime(): number {
    return this.startTime;
  }

  setAuthToken(authToken: string): void {
    this.authToken = authToken;
    this.configureHandler();
  }

  setActivityData(activity: ClickstreamActivityData): void {
    this.activityData = activity;
    if (this.clickstreamCache) {
      this.clickstreamCache.activity = activity;
    }
  }

  configureHandler(): void {
    this.apiHandler.configure({
      baseUrl: this.configService.config.olpApiDomain,
      defaultHeaders: {
        Authorization: 'Bearer ' + this.authToken,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    this.apiHandler.messageService = this.messageService;
    this.apiHandler.loggingService = this.loggingService;
  }

  initializeClickstreamCache(
    activityData: ClickstreamActivityData,
    scoreActivity: boolean
  ): void {
    this.setActivityData(activityData);
    this.clickstreamCache = {
      schoolYear: this.schoolYear,
      sessionId: this.sessionId,
      scoreActivity: scoreActivity,
      startTime: new Date(this.startTime).toISOString(),
      activity: this.activityData,
      events: [],
    };
  }

  async startSession(activityName: string): Promise<void> {
    if (!this.sessionId) {
      return Promise.reject('Invalid Session ID');
    }
    const sessionStart: ClickstreamEventData = {
      eventType: ClickstreamEventTypeName.SessionStart,
      key: activityName,
      offset: 0,
      data: {
        schoolYear: this.schoolYear,
        sessionId: this.sessionId,
      },
    };
    this.addEvent(sessionStart);
  }

  setSupervisorContext(subjectName: string): void {
    this.addEvent({
      eventType: ClickstreamEventTypeName.SupervisorContext,
      key: 'Context',
      offset: 0,
      data: subjectName,
    });
  }

  initializeActivity(
    activityData: ClickstreamActivityData,
    scoreActivity: boolean
  ): void {
    this.initializeClickstreamCache(activityData, scoreActivity);
    this.addEvent({
      eventType: ClickstreamEventTypeName.ActivityBegin,
      key: activityData.activityName,
      offset: 0,
      data: activityData.activityName,
    });
  }

  continueAmiraActivity(inboxActivityId: string): void {
    // add the inboxActivityId to the root
    if (this.clickstreamCache) {
      this.clickstreamCache.amiraActivityId = inboxActivityId;
    }
    // add continue event
    this.addEvent({
      eventType: ClickstreamEventTypeName.Event,
      key: ClickstreamEventTypeName.AmiraActivityContinue,
      offset: 0,
      data: inboxActivityId,
    });
  }

  completeActivity(score: number, numStars: number, offset: number): void {
    this.addEvent({
      eventType: ClickstreamEventTypeName.ActivityComplete,
      key: this.activityData.activityName,
      offset,
      data: [this.activityData.activityName, numStars, score],
    });
  }

  completeAmiraActivity(
    score: number,
    offset: number,
    amiraActivityId: string
  ): void {
    this.addEvent({
      eventType: ClickstreamEventTypeName.Event,
      key: ClickstreamEventTypeName.AmiraActivityComplete,
      offset,
      data: [amiraActivityId, score],
    });
  }

  addEvent(event: ClickstreamEventData): void {
    const eventType = event.eventType;
    if (
      Object.keys(ClickstreamEventTypeName).includes(eventType) &&
      this.clickstreamCache !== undefined
    ) {
      this.clickstreamCache.events.push(event);
    }
  }

  clearEvents(): void {
    if (this.clickstreamCache) {
      this.clickstreamCache.events = [];
    }
  }

  clearCache(): void {
    this.clickstreamCache = undefined;
  }

  saveClickstreamData(): void {
    if (this.clickstreamCache && this.clickstreamCache.events.length > 0) {
      this.apiHandler.post(this.buildPath(), {
        data: this.clickstreamCache,
      });
    } else {
      console.error(
        "We tried to save clickstream data, but we don't have a valid clickstream cache or events.",
        this.clickstreamCache
      );
    }
  }

  private buildPath(): string {
    return `${this.pathBase}/${this.userId}`;
  }
  /**
   * This is for debug purposes only!
   */
  get clickstreamCacheData(): ClickstreamStore | undefined {
    return this.clickstreamCache;
  }
}
